
import React, { useState } from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Plus } from 'lucide-react';
import { cn } from "@/lib/utils";

interface SpecialDate {
  id: number;
  date: Date;
  availability: string;
}

interface SamplesFormProps {
  readOnly?: boolean;
}

const SamplesForm = ({ readOnly = false }: SamplesFormProps) => {
  const { register } = useFormContext();
  const [registrationDate, setRegistrationDate] = useState<Date>(new Date());
  const [specialDates, setSpecialDates] = useState<SpecialDate[]>([]);

  const handleAddSpecialDate = () => {
    if (readOnly) return;
    const newSpecialDate: SpecialDate = {
      id: Date.now(),
      date: new Date(),
      availability: ''
    };
    setSpecialDates([...specialDates, newSpecialDate]);
  };

  const handleSpecialDateChange = (id: number, field: keyof SpecialDate, value: any) => {
    if (readOnly) return;
    setSpecialDates(specialDates.map(date => 
      date.id === id ? { ...date, [field]: value } : date
    ));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label>Fecha de muestra *</Label>
          <Popover>
            <PopoverTrigger asChild disabled={readOnly}>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !registrationDate && "text-muted-foreground",
                  readOnly && "bg-gray-100 cursor-not-allowed"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {registrationDate ? format(registrationDate, "PPP") : <span>Seleccione una fecha</span>}
              </Button>
            </PopoverTrigger>
            {!readOnly && (
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={registrationDate}
                  onSelect={(date) => date && setRegistrationDate(date)}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            )}
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="paxLastNight">PAX anoche *</Label>
          <Input 
            type="number" 
            placeholder="Ej. 15" 
            {...register('samples.paxLastNight')} 
            disabled={readOnly}
            className={readOnly ? "bg-gray-100" : ""}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="occupiedRooms">Habitaciones ocupadas *</Label>
          <Input 
            type="number" 
            placeholder="Ej. 8" 
            {...register('samples.occupiedRooms')} 
            disabled={readOnly}
            className={readOnly ? "bg-gray-100" : ""}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="occupancyPercentage">Porcentaje de ocupación</Label>
          <Input type="text" value="Calculado" disabled className="bg-gray-100" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Fechas especiales</Label>
          {!readOnly && (
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1"
              onClick={handleAddSpecialDate}
            >
              <Plus className="h-4 w-4" />
              Agregar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {specialDates.map((specialDate) => (
            <div key={specialDate.id} className="space-y-0 flex gap-4">
              <div className="flex-1">
                <Popover>
                  <PopoverTrigger asChild disabled={readOnly}>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !specialDate.date && "text-muted-foreground",
                        readOnly && "bg-gray-100 cursor-not-allowed"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {specialDate.date ? format(specialDate.date, "PPP") : <span>Seleccione una fecha</span>}
                    </Button>
                  </PopoverTrigger>
                  {!readOnly && (
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={specialDate.date}
                        onSelect={(date) => date && handleSpecialDateChange(specialDate.id, 'date', date)}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  )}
                </Popover>
              </div>
              <div className="flex-1">
                <Input 
                  placeholder="Reservas / Disponibilidad"
                  value={specialDate.availability}
                  onChange={(e) => handleSpecialDateChange(specialDate.id, 'availability', e.target.value)}
                  disabled={readOnly}
                  className={readOnly ? "bg-gray-100" : ""}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="observations">Observaciones</Label>
        <Textarea 
          placeholder="Ingrese observaciones adicionales..." 
          className={cn("min-h-[100px]", readOnly && "bg-gray-100")}
          {...register('samples.observations')}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default SamplesForm;
